import { useEffect, useRef, useState } from "react";
import b1 from "../assets/B1.svg";
import b2 from "../assets/B2.svg";
import b3 from "../assets/B3.svg";
import tick from "../assets/Tick.svg";
import u1 from "../assets/U1.svg";
import u2 from "../assets/U2.svg";
import u3 from "../assets/U3.svg";
import u4 from "../assets/U4.svg";
import u5 from "../assets/U5.svg";
import u6 from "../assets/U6.svg";
import { Helmet } from "react-helmet-async";
import Digitall from "../assets/digital.webp";

import { motion } from "framer-motion";

const Digital = () => {
  const benefits = [
    {
      title: "Significant in Efficiency",
      description:
        "Organizations can automate repetitive operations that were previously completed by hand by translating data into digital formats.",
      image: b1,
    },
    {
      title: "Improved efficiency",
      description:
        "Evaluate information more efficiently thanks to improved administration and accessibility brought about by digitizing data.",
      image: b2,
    },
    {
      title: "Time saving",
      description:
        "Employee attention can be directed toward more strategic and value-added tasks by minimizing manual intervention.",
      image: b3,
    },
  ];

  const features = [
    "Saves time on manual processing by transforming all data types into digital ones.",
    "Allows companies to respond swiftly to changes in the market and internal demands.",
    "Reduces the possibility of human error.",
    "Showcases LENTERA’s proficiency with digital tools.",
    <>
      Results in more innovative, efficient,
      <br /> and flexible operations.
    </>,
  ];

  const industryData = [
    {
      icon: u1,
      title: "Manufacturing",
      description:
        "By enhancing production effectiveness, quality assurance, and predictive maintenance, automated data handling can lower operating expenses and downtime.",
    },
    {
      icon: u2,
      title: "Healthcare",
      description:
        "Management of patient records, expedites administrative procedures, and enables sophisticated analytics to improve patient outcomes and operational effectiveness.",
    },
    {
      icon: u3,
      title: "Finance",
      description:
        "The project can expedite and improve the accuracy of financial operations by optimizing transaction processing, risk management, and compliance reporting.",
    },
    {
      icon: u4,
      title: "Education",
      description:
        "Educational establishments can use digital data to increase research capacity, track student performance more precisely, and improve administrative efficiency.",
    },
    {
      icon: u5,
      title: "Transportation and distribution",
      description:
        "Automation and digitization in transportation can enhance monitoring, inventory control, and supply chain synchronization, resulting in quicker delivery times and fewer mistakes.",
    },
    {
      icon: u6,
      title: "Retail",
      description:
        "Digital transformation of retail operations, which also boosts supply chain efficiency and increases consumer happiness through tailored marketing.",
    },
  ];

  const itemRefs = useRef([]);
  const [visibleItems, setVisibleItems] = useState([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute("data-index"));
            setVisibleItems((prev) => {
              if (!prev.includes(index)) return [...prev, index];
              return prev;
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    itemRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div>
      <Helmet>
        <title>
          LENTERA enterprise applications, business process optimization,
          enterprise software development.
        </title>
        <meta
          name="description"
          content="Automate processes, enhance operational efficiency, and reduce errors across industries like manufacturing, healthcare, finance, education, transportation, and retail."
        />
        <meta
          name="keywords"
          content="LENTERA digital innovation, workflow digitization, industry-specific digital transformation, digital tools for business, reduce manual processes, improve business performance, technology-driven growth"
        />
      </Helmet>

      <section className="w-full bg-[#FAFAFA] py-20 px-4 sm:px-6">
        <div className="w-full max-w-[1312px] mx-auto flex flex-col lg:flex-row items-center justify-between gap-10">
          {/* Left Column – 60% with animation */}
          <motion.div
            className="w-full lg:w-[60%]"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h5 className="text-[#212129] text-sm md:text-[15px] font-semibold uppercase mb-3 tracking-wide">
              Digital Transformation
            </h5>

            <h2 className="text-[#212129] text-[40px] md:text-[48px] font-extrabold leading-tight mb-6">
              Transforming business
              <br className="hidden sm:block" /> through technology
            </h2>

            <p className="text-[#212129] text-base leading-relaxed max-w-[580px]">
              To increase automation, productivity, and efficiency, a digital
              transformation project entails transferring all data and processes
              into digital formats.
            </p>
          </motion.div>

          {/* Right Column – 40% with animation */}
          <motion.div
            className="w-full lg:w-[40%] flex justify-center"
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          >
            <img
              src={Digitall}
              alt="Digital Transformation"
              className="w-454 max-w-[450px] h-auto object-contain"
            />
          </motion.div>
        </div>
      </section>
      <section className="bg-black rounded-[2rem] px-4 py-8 md:px-8 md:py-10 lg:px-12 lg:py-12 text-white font-poppins max-w-5xl mx-auto mt-12 overflow-hidden">
        <style>{`
    @keyframes gradientShift {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    .animate-gradient {
      background-size: 200% 200%;
      animation: gradientShift 5s ease infinite;
    }
  `}</style>

        <div className="text-center">
          <h2
            className="text-2xl md:text-3xl lg:text-5xl font-extrabold mb-4 lg:mb-8
      bg-gradient-to-r from-[#B540FF] via-pink-500 to-[#42E8E0]
      inline-block text-transparent bg-clip-text animate-gradient"
          >
            Overview
          </h2>

          <p className="text-sm md:text-base lg:text-[16px] weight-[400] leading-relaxed max-w-2xl mx-auto whitespace-pre-line">
            LENTERA is a leader in technology innovation and is always looking
            for new ways to improve company processes by implementing digital
            transformation. Digital data formats, including mean, deviation, and
            multiple values, are the subject of our most recent study.
          </p>
        </div>
      </section>

      <div className="bg-white text-gray-900 px-6 py-12 font-[Poppins] mt-10 mx-4 sm:mx-6 md:mx-10 lg:mx-5">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Left Heading */}
          <div>
            <h1 className="text-4xl sm:text-4xl font-semibold leading-tight">
              Benefits of digital
              <br />
              transformation
            </h1>
          </div>

          {/* Right Paragraph */}
          <div>
            <p className="text-[12px] sm:text-[14px] md:text-[16px] leading-relaxed">
              Providing top-notch digital solutions is Lentera’s objective, and
              this digital transformation project offers many benefits that
              support it.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white py-12 px-4 sm:px-6 lg:px-8 font-[Poppins] max-w-full">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="flex flex-col items-start p-6 bg-white shadow-sm rounded-2xl border border-gray-100"
            >
              {/* Icon Section */}
              <div className="w-full bg-gray-100 rounded-xl p-4 flex justify-center mb-4">
                <img
                  src={benefit.image}
                  alt={benefit.title}
                  className="h-20 object-contain"
                />
              </div>
              {/* Title */}
              <h3 className="text-sm md:text-lg lg:text-[20px] leading-[24px] font-semibold mb-4">
                {benefit.title}
              </h3>
              {/* Description */}
              <p className="text-xs md:text-sm lg:text-[15px] text-normal leading-[24px] text-[#212129]">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      <section className="bg-[#1c1c1c] text-white font-[Poppins] py-10 px-6 mb-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-10">
          {/* Left Section */}
          <div className="relative">
            <div className="inline-block bg-black text-white text-sm font-semibold px-4 py-1 rounded-full mb-6">
              LENTERA’S INNOVATION
            </div>
            <h2 className="text-4xl md:text-4xl width-[670px] height-[120px] font-semibold font-medium mb-8 leading-tight">
              Digital transformation <br /> project Lentera's innovation
            </h2>
            <p className="text-gray-300 font-[400] leading-[16px] text-[13px]">
              Lentera’s innovation drives digital transformation by seamlessly
              integrating cutting-
              <br /> edge technology to enhance operational efficiency and
              foster sustainable growth.
            </p>
          </div>

          {/* Right Section */}
          <div className="px-0 md:px-6 py-0 md:py-12">
            <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-5">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className={`bg-[#3a3a3a] rounded-2xl p-6 flex items-start gap-4 ${
                    index === features.length - 1 ? "md:col-span-2" : ""
                  }`}
                >
                  {/* Tick Icon */}
                  <img src={tick} alt="tick" className="w-10 h-10 mt-1" />
                  {/* Feature Text */}
                  <p className="text-white text-sm leading-relaxed">
                    {feature}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-12 md:px-12 bg-white font-[Poppins] overflow-x-hidden">
        <style>{`
        @keyframes slideInRight {
          0% {
            transform: translateX(50px);
            opacity: 0;
          }
          100% {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes slideInLeft {
          0% {
            transform: translateX(-50px);
            opacity: 0;
          }
          100% {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.6s forwards;
        }
        .animate-slide-in-left {
          animation: slideInLeft 0.6s forwards;
        }
        .opacity-0 {
          opacity: 0;
        }
      `}</style>

        <div className="max-w-6xl mx-auto text-left">
          <h2 className="text-3xl md:text-4xl weight-[600] height-148px] font-bold text-[#1c1c1c] mb-12">
            Uses and advantages in industry
          </h2>
          <div className="grid grid-cols-1 width-[422px] height-[261px] sm:grid-cols-2 lg:grid-cols-3 gap-6 text-left">
            {industryData.map((item, index) => {
              const isFirstRow = index < 3;
              const isVisible = visibleItems.includes(index);

              return (
                <div
                  key={index}
                  data-index={index}
                  ref={(el) => (itemRefs.current[index] = el)}
                  className={`bg-[#eef4ff] rounded-2xl p-6 flex flex-col gap-3
                  ${
                    isVisible
                      ? isFirstRow
                        ? "animate-slide-in-right"
                        : "animate-slide-in-left"
                      : "opacity-0"
                  }
                `}
                  style={{ animationDelay: `${index * 0.15}s` }}
                >
                  <img src={item.icon} alt={item.title} className="w-10 h-10" />
                  <h3 className="font-semibold weight-[600] height-[20px] text-[#1c1c1c] text-base">
                    {item.title}
                  </h3>
                  <p className="text-[12px] md:text-sm lg:text-[15px] weight-[400] leading-normal md:leading-[24px] height-[15px] text-[#1c1c1c]">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Digital;
